import React, {
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useState,
} from "react";

import { Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Visitor } from "../../constants/common";
import Accordion from "../accordian";
import { Button } from "../button";
import VisitorsGroupTable from "../table/VisitorsGroupTable";
import VisitorDetailsGroupCard from "./VisitorDetailsGroupCard";
import { UseFormClearErrors } from "react-hook-form";

interface VisitorsGroupProps {
  addedVisitors: Visitor[];
  setAddedVisitors: Dispatch<SetStateAction<Visitor[]>>;
  // isVehicleInfoVisible: boolean;

  uploadPhotoRef: React.MutableRefObject<HTMLInputElement | null>;
  photoFileName: string;
  setPhotoFileName: React.Dispatch<React.SetStateAction<string>>;
  setPhotoFileType: Dispatch<SetStateAction<string>>;

  uploadEidRef: React.MutableRefObject<HTMLInputElement | null>;
  eidFileName: string;
  setEidFileName: React.Dispatch<React.SetStateAction<string>>;
  setEidFileType: Dispatch<SetStateAction<string>>;

  uploadPassportRef: MutableRefObject<HTMLInputElement | null>;
  passportFileName: string;
  setPassportFileName: Dispatch<SetStateAction<string>>;
  setPassportFileType: Dispatch<SetStateAction<string>>;

  uploadVisaRef: MutableRefObject<HTMLInputElement | null>;
  visaFileName: string;
  setVisaFileName: Dispatch<SetStateAction<string>>;
  setVisaFileType: Dispatch<SetStateAction<string>>;

  iqamaFileType: string;
  eidFileType: string;
  passportFileType: string;
  photoFileType: string;
  vehicleRegLicenseFileType: string;
  idOtherFileType: string;
  visaFileType?: string;

  isVIP?: boolean;

  uploadVehicleRegLicenseRef: MutableRefObject<HTMLInputElement | null>;
  setVehicleRegLicenseFileName: Dispatch<SetStateAction<string>>;
  setVehicleRegLicenseFileType: Dispatch<SetStateAction<string>>;
  vehicleRegLicenseFileName: string;

  uploadIqamaRef: React.MutableRefObject<HTMLInputElement | null>;
  iqamaFileName: string;
  setIqamaFileName: React.Dispatch<React.SetStateAction<string>>;
  setIqamaFileType: Dispatch<SetStateAction<string>>;

  uploadIdOtherRef: React.MutableRefObject<HTMLInputElement | null>;
  idOtherFileName: string;
  setIdOtherFileName: React.Dispatch<React.SetStateAction<string>>;
  setIdOtherFileType: Dispatch<SetStateAction<string>>;

  uploadHostEmployeeCardRef: MutableRefObject<HTMLInputElement | null>;
  hostEmployeeCardFileName: string;
  hostEmployeeCardFileType: string;
  setHostEmployeeCardFileName: Dispatch<SetStateAction<string>>;
  setHostEmployeeCardFileType: Dispatch<SetStateAction<string>>;

  visitEndDate: Date;

  cardNumber: number;

  clearErrors: UseFormClearErrors<{
    visitingDepartment?: string | undefined;
    gate?: string | undefined;
    meetingRoom?: string | undefined;
    visitPurpose?: string | undefined;
    visitPurposeOther?: string | undefined;
    plateSource?: string | undefined;
    plateNumber?: string | null | undefined;
    plateLetter1?: string | undefined;
    plateLetter2?: string | undefined;
    plateLetter3?: string | undefined;
    note?: string | undefined;
    location: string;
    building: string;
    visitReqType: string;
    hostEmail: string;
    hostMobileNumber: string;
    visitStartDate: string;
    visitEndDate: string;
  }>;
}

const VisitorsGroupCard = ({
  addedVisitors,
  setAddedVisitors,
  uploadPhotoRef,
  photoFileName,
  setPhotoFileName,
  setPhotoFileType,
  uploadEidRef,
  eidFileName,
  setEidFileName,
  setEidFileType,
  uploadPassportRef,
  passportFileName,
  setPassportFileName,
  setPassportFileType,

  uploadVisaRef,
  visaFileName,
  setVisaFileName,
  setVisaFileType,
  visaFileType,

  // isVehicleInfoVisible,
  eidFileType,
  passportFileType,
  photoFileType,
  isVIP,

  vehicleRegLicenseFileType,
  uploadVehicleRegLicenseRef,
  setVehicleRegLicenseFileName,
  setVehicleRegLicenseFileType,
  vehicleRegLicenseFileName,

  uploadIqamaRef,
  iqamaFileName,
  iqamaFileType,
  setIqamaFileName,
  setIqamaFileType,

  uploadIdOtherRef,
  idOtherFileName,
  idOtherFileType,
  setIdOtherFileName,
  setIdOtherFileType,

  uploadHostEmployeeCardRef,
  hostEmployeeCardFileName,
  hostEmployeeCardFileType,
  setHostEmployeeCardFileName,
  setHostEmployeeCardFileType,

  visitEndDate,
  cardNumber,

  clearErrors,
}: VisitorsGroupProps) => {
  const [isAddVisitorFormVisible, setIsAddVisitorFormVisible] =
    useState<boolean>(true);
  const [selectedKey, setSelectedKey] = useState<string>("1");

  const { t } = useTranslation();

  const [editingVisitor, setEditingVisitor] = useState<Visitor | null>(null);

  const handleFormReset = () => {
    setIsAddVisitorFormVisible(false);
    setEditingVisitor(null);
  };

  return (
    <div className="bg-white p-2 md:p-4 rounded-xl flex flex-col gap-3 lg:gap-6 shadow-[0px_1px_5px_0px_rgba(113,112,163,0.3)]">
      <Accordion
        id="1"
        selectedKey={selectedKey}
        onSelectionChange={setSelectedKey}
        title={
          <h1 className="text-xl md:text-2xl flex flex-1 items-end -mt-2">
            {t(`${cardNumber}`)}. {t("visitorsGroup")}
          </h1>
        }
      >
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between">
            <div />
            <Button
              type="button"
              onClick={() => {
                setIsAddVisitorFormVisible(false);
                setEditingVisitor(null);
                setTimeout(() => {
                  setIsAddVisitorFormVisible(true);
                }, 0);
              }}
              Icon={<Plus size={20} />}
            >
              {t("addVisitor")}
            </Button>
          </div>

          <VisitorsGroupTable
            addedVisitors={addedVisitors}
            setAddedVisitors={setAddedVisitors}
            setEditingVisitor={setEditingVisitor}
            setIsAddVisitorFormVisible={setIsAddVisitorFormVisible}
            onFormReset={handleFormReset}
          />

          {isAddVisitorFormVisible && (
            <VisitorDetailsGroupCard
              uploadPhotoRef={uploadPhotoRef}
              photoFileName={photoFileName}
              setPhotoFileName={setPhotoFileName}
              uploadEidRef={uploadEidRef}
              eidFileName={eidFileName}
              setEidFileName={setEidFileName}
              setIsAddVisitorFormVisible={setIsAddVisitorFormVisible}
              setAddedVisitors={setAddedVisitors}
              setPhotoFileType={setPhotoFileType}
              setEidFileType={setEidFileType}
              uploadPassportRef={uploadPassportRef}
              passportFileName={passportFileName}
              setPassportFileName={setPassportFileName}
              setPassportFileType={setPassportFileType}
              eidFileType={eidFileType}
              passportFileType={passportFileType}
              uploadVisaRef={uploadVisaRef}
              visaFileName={visaFileName}
              setVisaFileName={setVisaFileName}
              setVisaFileType={setVisaFileType}
              visaFileType={visaFileType}
              photoFileType={photoFileType}
              isVip={isVIP}
              uploadVehicleRegLicenseRef={uploadVehicleRegLicenseRef}
              setVehicleRegLicenseFileName={setVehicleRegLicenseFileName}
              setVehicleRegLicenseFileType={setVehicleRegLicenseFileType}
              vehicleRegLicenseFileName={vehicleRegLicenseFileName}
              vehicleRegLicenseFileType={vehicleRegLicenseFileType}
              uploadIqamaRef={uploadIqamaRef}
              iqamaFileName={iqamaFileName}
              setIqamaFileName={setIqamaFileName}
              setIqamaFileType={setIqamaFileType}
              iqamaFileType={iqamaFileType}
              uploadIdOtherRef={uploadIdOtherRef}
              idOtherFileName={idOtherFileName}
              setIdOtherFileName={setIdOtherFileName}
              setIdOtherFileType={setIdOtherFileType}
              idOtherFileType={idOtherFileType}
              uploadHostEmployeeCardRef={uploadHostEmployeeCardRef}
              hostEmployeeCardFileName={hostEmployeeCardFileName}
              hostEmployeeCardFileType={hostEmployeeCardFileType}
              setHostEmployeeCardFileName={setHostEmployeeCardFileName}
              setHostEmployeeCardFileType={setHostEmployeeCardFileType}
              visitEndDate={visitEndDate}
              editingVisitor={editingVisitor}
              setEditingVisitor={setEditingVisitor}
              clearErrors={clearErrors}
            />
          )}
        </div>
      </Accordion>
    </div>
  );
};

export default VisitorsGroupCard;
